import type {
  AtividadeHoraria,
  CargaAtualAtendimento,
  CargaMensalAtendimento,
  DashboardAtendimentoData,
  DesempenhoAtendente,
  GranularidadeSerieAtendimento,
  PeriodoDashboardAtendimento,
  PontoSerieAtendimento,
  TempoRespostaAposTransferencia,
} from '../types/dashboardAtendimento';

/**
 * Mock do dashboard de atendimento, no formato exato do endpoint que ainda não existe.
 *
 * Determinístico por DATA, não por período: o volume de um dia sai de uma semente com a data dele,
 * então a mesma semana aparece igual em "7 dias" e dentro de "30 dias". Um mock que sorteasse por
 * chamada faria os números pularem a cada troca de filtro e ninguém conseguiria avaliar a tela.
 *
 * Os totais do resumo são SOMAS da série, nunca sorteados à parte — é o que o backend também vai
 * precisar garantir, e o que mantém o gráfico batendo com o card.
 */

/** Brasil sem horário de verão desde 2019: o deslocamento é fixo. */
const OFFSET_SAO_PAULO_MS = 3 * 60 * 60 * 1000;
const MINUTO_MS = 60 * 1000;
const MESES_DE_HISTORICO = 14;
const MESES_DA_CARGA_MENSAL = 12;
const ATENDENTES_POR_CONVERSA = 1.08;

const DIAS_POR_PERIODO: Record<Exclude<PeriodoDashboardAtendimento, 'all'>, number> = {
  '1d': 1,
  '7d': 7,
  '30d': 30,
  '60d': 60,
  '90d': 90,
};

const ATENDENTES_MOCK = [
  { usuarioId: 'mock-atendente-1', nome: 'Carla Menezes', peso: 0.29, horaDePico: 10, tempo: 9 },
  { usuarioId: 'mock-atendente-2', nome: 'Rafael Souza', peso: 0.24, horaDePico: 14, tempo: 12 },
  { usuarioId: 'mock-atendente-3', nome: 'Juliana Prado', peso: 0.2, horaDePico: 9, tempo: 16 },
  { usuarioId: 'mock-atendente-4', nome: 'Marcos Lima', peso: 0.16, horaDePico: 16, tempo: 21 },
  { usuarioId: 'mock-atendente-5', nome: 'Beatriz Castro', peso: 0.11, horaDePico: 11, tempo: 27 },
];

/** Quando os clientes escrevem: o dia todo, com cauda à noite. */
const PESO_ENTRADA_POR_HORA = [
  0.3, 0.2, 0.1, 0.1, 0.1, 0.2, 0.6, 1.8, 4.2, 6.1, 6.8, 6.3, 4.4, 4.9, 6.2, 6.4, 5.9, 5.1, 4.0,
  3.4, 2.9, 2.1, 1.2, 0.6,
];

/** Quando a equipe responde: horário comercial, com queda no almoço. */
const PESO_ATIVIDADE_POR_HORA = [
  0, 0, 0, 0, 0, 0, 0.2, 1.2, 4.5, 7.8, 8.6, 8.1, 4.2, 4.9, 7.6, 8.0, 7.1, 5.3, 2.6, 1.4, 0.9, 0.5,
  0.2, 0,
];

interface DataCivil {
  ano: number;
  mes: number;
  dia: number;
}

interface VolumeDoIntervalo {
  atendimentos: number;
  interceptadosPelaIa: number;
  transferidosParaHumano: number;
}

function criarAleatorio(semente: string): () => number {
  let hash = 1779033703 ^ semente.length;
  for (let indice = 0; indice < semente.length; indice += 1) {
    hash = Math.imul(hash ^ semente.charCodeAt(indice), 3432918353);
    hash = (hash << 13) | (hash >>> 19);
  }
  let estado = hash >>> 0;
  return () => {
    estado = (estado + 0x6d2b79f5) | 0;
    let t = Math.imul(estado ^ (estado >>> 15), 1 | estado);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const umaCasa = (valor: number): number => Math.round(valor * 10) / 10;
const doisDigitos = (valor: number): string => String(valor).padStart(2, '0');
const somar = (valores: number[]): number => valores.reduce((total, valor) => total + valor, 0);

function dataCivilEmSaoPaulo(instante: Date): DataCivil {
  const local = new Date(instante.getTime() - OFFSET_SAO_PAULO_MS);
  return { ano: local.getUTCFullYear(), mes: local.getUTCMonth() + 1, dia: local.getUTCDate() };
}

function horaEmSaoPaulo(instante: Date): number {
  return new Date(instante.getTime() - OFFSET_SAO_PAULO_MS).getUTCHours();
}

function somarDias(data: DataCivil, dias: number): DataCivil {
  const resultado = new Date(Date.UTC(data.ano, data.mes - 1, data.dia + dias));
  return {
    ano: resultado.getUTCFullYear(),
    mes: resultado.getUTCMonth() + 1,
    dia: resultado.getUTCDate(),
  };
}

function isoSaoPaulo(data: DataCivil, hora = 0): string {
  return `${data.ano}-${doisDigitos(data.mes)}-${doisDigitos(data.dia)}T${doisDigitos(hora)}:00:00-03:00`;
}

function mesmoDia(a: DataCivil, b: DataCivil): boolean {
  return a.ano === b.ano && a.mes === b.mes && a.dia === b.dia;
}

function diasNoMes(ano: number, mes: number): number {
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate();
}

function primeiroDoMes(data: DataCivil, mesesAtras: number): DataCivil {
  const resultado = new Date(Date.UTC(data.ano, data.mes - 1 - mesesAtras, 1));
  return { ano: resultado.getUTCFullYear(), mes: resultado.getUTCMonth() + 1, dia: 1 };
}

function diasEntre(inicio: DataCivil, fim: DataCivil): number {
  const umDia = 24 * 60 * MINUTO_MS;
  const inicioMs = Date.UTC(inicio.ano, inicio.mes - 1, inicio.dia);
  const fimMs = Date.UTC(fim.ano, fim.mes - 1, fim.dia);
  return Math.round((fimMs - inicioMs) / umDia) + 1;
}

/** Garante a regra do contrato: IA e transferência nunca passam do total do intervalo. */
function volumeCoerente(
  atendimentos: number,
  interceptados: number,
  transferidos: number,
): VolumeDoIntervalo {
  const interceptadosPelaIa = Math.min(Math.max(interceptados, 0), atendimentos);
  const transferidosParaHumano = Math.min(
    Math.max(transferidos, 0),
    atendimentos - interceptadosPelaIa,
  );
  return { atendimentos, interceptadosPelaIa, transferidosParaHumano };
}

function volumeDoDiaCompleto(data: DataCivil): VolumeDoIntervalo {
  const aleatorio = criarAleatorio(`dia:${isoSaoPaulo(data)}`);
  const diaSemana = new Date(Date.UTC(data.ano, data.mes - 1, data.dia)).getUTCDay();
  const fimDeSemana = diaSemana === 0 || diaSemana === 6;
  const atendimentos = Math.round((fimDeSemana ? 11 : 36) * (0.75 + aleatorio() * 0.5));
  return volumeCoerente(
    atendimentos,
    Math.round(atendimentos * (0.5 + aleatorio() * 0.12)),
    Math.round(atendimentos * (0.3 + aleatorio() * 0.08)),
  );
}

/** Hoje ainda não acabou: só as horas até a atual têm movimento. */
function serieHorariaDeHoje(hoje: DataCivil, horaAgora: number): PontoSerieAtendimento[] {
  const volume = volumeDoDiaCompleto(hoje);
  const somaPesos = somar(PESO_ENTRADA_POR_HORA);

  return Array.from({ length: horaAgora + 1 }, (_, hora) => {
    const fracao = PESO_ENTRADA_POR_HORA[hora] / somaPesos;
    return {
      inicio: isoSaoPaulo(hoje, hora),
      ...volumeCoerente(
        Math.round(volume.atendimentos * fracao),
        Math.round(volume.interceptadosPelaIa * fracao),
        Math.round(volume.transferidosParaHumano * fracao),
      ),
    };
  });
}

function somarVolumes(pontos: VolumeDoIntervalo[]): VolumeDoIntervalo {
  return {
    atendimentos: somar(pontos.map((ponto) => ponto.atendimentos)),
    interceptadosPelaIa: somar(pontos.map((ponto) => ponto.interceptadosPelaIa)),
    transferidosParaHumano: somar(pontos.map((ponto) => ponto.transferidosParaHumano)),
  };
}

function volumeDoDia(data: DataCivil, hoje: DataCivil, horaAgora: number): VolumeDoIntervalo {
  if (mesmoDia(data, hoje)) return somarVolumes(serieHorariaDeHoje(hoje, horaAgora));
  return volumeDoDiaCompleto(data);
}

function serieDiaria(
  primeiroDia: DataCivil,
  dias: number,
  hoje: DataCivil,
  horaAgora: number,
): PontoSerieAtendimento[] {
  return Array.from({ length: dias }, (_, indice) => {
    const data = somarDias(primeiroDia, indice);
    return { inicio: isoSaoPaulo(data), ...volumeDoDia(data, hoje, horaAgora) };
  });
}

function volumeDoMes(inicioDoMes: DataCivil, hoje: DataCivil, horaAgora: number) {
  const ultimoDia =
    inicioDoMes.ano === hoje.ano && inicioDoMes.mes === hoje.mes
      ? hoje.dia
      : diasNoMes(inicioDoMes.ano, inicioDoMes.mes);
  const dias = Array.from({ length: ultimoDia }, (_, indice) =>
    volumeDoDia(somarDias(inicioDoMes, indice), hoje, horaAgora),
  );
  return { volume: somarVolumes(dias), diasConsiderados: ultimoDia };
}

function serieMensal(meses: number, hoje: DataCivil, horaAgora: number): PontoSerieAtendimento[] {
  return Array.from({ length: meses }, (_, indice) => {
    const inicioDoMes = primeiroDoMes(hoje, meses - 1 - indice);
    return {
      inicio: isoSaoPaulo(inicioDoMes),
      ...volumeDoMes(inicioDoMes, hoje, horaAgora).volume,
    };
  });
}

function montarAtendentes(
  atendimentosComHumano: number,
  dias: number,
  semente: string,
): DesempenhoAtendente[] {
  const aleatorio = criarAleatorio(`atendentes:${semente}`);

  return ATENDENTES_MOCK.map((atendente) => {
    const atendimentos = Math.round(
      atendimentosComHumano * atendente.peso * ATENDENTES_POR_CONVERSA,
    );
    return {
      usuarioId: atendente.usuarioId,
      nome: atendente.nome,
      atendimentos,
      mediaAtendimentosPorDia: umaCasa(atendimentos / dias),
      mensagensEnviadas: Math.round(atendimentos * (3.6 + aleatorio() * 1.4)),
      tempoMedioRespostaMinutos:
        atendimentos > 0 ? Math.round(atendente.tempo * (0.85 + aleatorio() * 0.3)) : null,
      horaDePico: atendimentos > 0 ? atendente.horaDePico : null,
    };
  })
    .filter((atendente) => atendente.atendimentos > 0)
    .sort((a, b) => b.atendimentos - a.atendimentos);
}

/**
 * Distribui as mensagens enviadas pelas horas do dia. O resto do arredondamento vai para a hora de
 * maior peso, para a soma por hora bater exatamente com a soma por atendente.
 */
function montarAtividadePorHora(
  mensagensEnviadas: number,
  atendentesAtivos: number,
  dias: number,
  horaLimite: number,
): AtividadeHoraria[] {
  const pesos = PESO_ATIVIDADE_POR_HORA.map((peso, hora) => (hora <= horaLimite ? peso : 0));
  const somaPesos = somar(pesos);
  if (somaPesos === 0 || mensagensEnviadas === 0) {
    return pesos.map((_, hora) => ({ hora, mensagensEnviadas: 0, atendentesAtivos: 0 }));
  }

  const porHora = pesos.map((peso) => Math.floor((mensagensEnviadas * peso) / somaPesos));
  const horaDeMaiorPeso = pesos.indexOf(Math.max(...pesos));
  porHora[horaDeMaiorPeso] += mensagensEnviadas - somar(porHora);
  const pesoMaximo = Math.max(...pesos);

  return porHora.map((mensagens, hora) => ({
    hora,
    mensagensEnviadas: mensagens,
    atendentesAtivos:
      mensagens > 0
        ? Math.max(1, Math.round(atendentesAtivos * (pesos[hora] / pesoMaximo) * dias))
        : 0,
  }));
}

function montarTempoResposta(
  transferidos: number,
  semente: string,
): TempoRespostaAposTransferencia {
  if (transferidos === 0) {
    return {
      mediaMinutos: null,
      medianaMinutos: null,
      p90Minutos: null,
      respondidas: 0,
      semResposta: 0,
    };
  }
  const aleatorio = criarAleatorio(`tempo:${semente}`);
  const semResposta = Math.round(transferidos * 0.04);
  return {
    mediaMinutos: Math.round(12 + aleatorio() * 5),
    medianaMinutos: Math.round(6 + aleatorio() * 3),
    p90Minutos: Math.round(30 + aleatorio() * 15),
    respondidas: transferidos - semResposta,
    semResposta,
  };
}

function montarCargaAtual(agora: Date, hoje: DataCivil): CargaAtualAtendimento {
  const aleatorio = criarAleatorio(`carga:${isoSaoPaulo(hoje)}`);
  const aguardandoHumano = Math.round(2 + aleatorio() * 5);
  return {
    comIa: Math.round(15 + aleatorio() * 15),
    aguardandoHumano,
    emAtendimentoHumano: Math.round(8 + aleatorio() * 7),
    aguardandoHumanoDesde:
      aguardandoHumano > 0
        ? new Date(agora.getTime() - Math.round(20 + aleatorio() * 35) * MINUTO_MS).toISOString()
        : null,
  };
}

function montarCargaMensal(hoje: DataCivil, horaAgora: number): CargaMensalAtendimento[] {
  return Array.from({ length: MESES_DA_CARGA_MENSAL }, (_, indice) => {
    const inicioDoMes = primeiroDoMes(hoje, MESES_DA_CARGA_MENSAL - 1 - indice);
    const { volume, diasConsiderados } = volumeDoMes(inicioDoMes, hoje, horaAgora);
    return {
      mes: `${inicioDoMes.ano}-${doisDigitos(inicioDoMes.mes)}`,
      ...volume,
      mediaAtendimentosPorDia: umaCasa(volume.atendimentos / diasConsiderados),
    };
  });
}

interface JanelaApurada {
  primeiroDia: DataCivil;
  dias: number;
  granularidade: GranularidadeSerieAtendimento;
  serie: PontoSerieAtendimento[];
  totalAnterior: number | null;
}

function apurarJanela(
  periodo: PeriodoDashboardAtendimento,
  hoje: DataCivil,
  horaAgora: number,
): JanelaApurada {
  if (periodo === 'all') {
    const primeiroDia = primeiroDoMes(hoje, MESES_DE_HISTORICO - 1);
    return {
      primeiroDia,
      dias: diasEntre(primeiroDia, hoje),
      granularidade: 'mes',
      serie: serieMensal(MESES_DE_HISTORICO, hoje, horaAgora),
      totalAnterior: null,
    };
  }

  if (periodo === '1d') {
    const ontem = somarDias(hoje, -1);
    return {
      primeiroDia: hoje,
      dias: 1,
      granularidade: 'hora',
      serie: serieHorariaDeHoje(hoje, horaAgora),
      totalAnterior: somarVolumes(serieHorariaDeHoje(ontem, horaAgora)).atendimentos,
    };
  }

  const dias = DIAS_POR_PERIODO[periodo];
  const primeiroDia = somarDias(hoje, -(dias - 1));
  const anterior = serieDiaria(somarDias(primeiroDia, -dias), dias, hoje, horaAgora);
  return {
    primeiroDia,
    dias,
    granularidade: 'dia',
    serie: serieDiaria(primeiroDia, dias, hoje, horaAgora),
    totalAnterior: somarVolumes(anterior).atendimentos,
  };
}

export function gerarDashboardAtendimentoMock(
  periodo: PeriodoDashboardAtendimento,
  agora: Date = new Date(),
): DashboardAtendimentoData {
  const hoje = dataCivilEmSaoPaulo(agora);
  const horaAgora = horaEmSaoPaulo(agora);
  const janela = apurarJanela(periodo, hoje, horaAgora);
  const semente = `${periodo}:${isoSaoPaulo(hoje)}`;

  const total = somarVolumes(janela.serie);
  const atendimentosComHumano = total.atendimentos - total.interceptadosPelaIa;
  const atendentes = montarAtendentes(atendimentosComHumano, janela.dias, semente);
  const atendentesAtivos = atendentes.length;
  const percentual = (parte: number) =>
    total.atendimentos > 0 ? umaCasa((parte / total.atendimentos) * 100) : 0;

  return {
    periodo: {
      chave: periodo,
      inicio: isoSaoPaulo(janela.primeiroDia),
      fim: agora.toISOString(),
      dias: janela.dias,
      granularidade: janela.granularidade,
    },
    resumo: {
      totalAtendimentos: total.atendimentos,
      mediaAtendimentosPorDia: umaCasa(total.atendimentos / janela.dias),
      atendentesAtivos,
      mediaAtendimentosPorAtendentePorDia:
        atendentesAtivos > 0 ? umaCasa(atendimentosComHumano / atendentesAtivos / janela.dias) : 0,
      interceptadosPelaIa: {
        total: total.interceptadosPelaIa,
        percentual: percentual(total.interceptadosPelaIa),
      },
      transferidosParaHumano: {
        total: total.transferidosParaHumano,
        percentual: percentual(total.transferidosParaHumano),
      },
      tempoRespostaAposTransferencia: montarTempoResposta(total.transferidosParaHumano, semente),
      variacaoTotalPercentual:
        janela.totalAnterior && janela.totalAnterior > 0
          ? umaCasa(((total.atendimentos - janela.totalAnterior) / janela.totalAnterior) * 100)
          : null,
    },
    serie: janela.serie,
    atendentes,
    atividadePorHora: montarAtividadePorHora(
      somar(atendentes.map((atendente) => atendente.mensagensEnviadas)),
      atendentesAtivos,
      janela.dias,
      periodo === '1d' ? horaAgora : 23,
    ),
    cargaAtual: montarCargaAtual(agora, hoje),
    cargaMensal: montarCargaMensal(hoje, horaAgora),
  };
}
